import {
  Loader2,
  Maximize,
  Minimize,
  Pause,
  PictureInPicture2,
  Play,
  RectangleHorizontal,
  RotateCcw,
  Settings,
  Volume2,
  VolumeX
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type PointerEvent as ReactPointerEvent
} from "react";

import { Tip } from "@/components/InfoTip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  enterWebkitVideoFullscreen,
  exitElementFullscreen,
  exitWebkitVideoFullscreen,
  getFullscreenElement,
  isAppleTouchDevice,
  isPortraitViewport,
  isWebkitVideoFullscreen,
  lockLandscapeOrientation,
  requestElementFullscreen,
  subscribeToOrientation,
  supportsElementFullscreen,
  unlockOrientation
} from "@/lib/fullscreen";
import { cn, formatDuration } from "@/lib/utils";
import {
  bindClipMediaSession,
  setClipMediaSessionPlaybackState,
  setClipMediaSessionPosition
} from "@/clip/mediaSession";

const VOLUME_STORAGE_KEY = "openvod.player.volume";
const THEATRE_STORAGE_KEY = "openvod.player.theatre";
const PLAYBACK_RATES = [0.5, 1, 1.25, 1.5, 2] as const;

/** How long the controls stay up after the pointer stops moving. Matches the app. */
const IDLE_HIDE_MS = 2_500;

/** Fade out/in when flipping theatre so the layout jump isn't a hard cut. */
const THEATRE_FADE_MS = 90;

/** Tiny internal buffer; the CSS blur hides the low resolution and keeps it cheap. */
const AMBIENT_SAMPLE_WIDTH = 160;

/** Per-frame callback API (not in every lib.dom); optional so we can fall back to rAF. */
type FrameCallbackVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: (now: number) => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

const readStoredVolume = (): number => {
  const raw = Number(window.localStorage.getItem(VOLUME_STORAGE_KEY));
  return Number.isFinite(raw) && raw >= 0 && raw <= 1 ? raw : 1;
};

// Theatre is on by default; only an explicit opt-out ("0") turns it off, so a
// first-time viewer gets the full-bleed layout.
const readStoredTheatre = (): boolean => window.localStorage.getItem(THEATRE_STORAGE_KEY) !== "0";

/** Fraction [0,1] along an element for a pointer event. */
const fractionAt = (el: HTMLElement, clientX: number): number => {
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0) return 0;
  return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
};

type ClipPlayerProps = {
  src: string;
  poster?: string | null;
  title: string;
  /** Duration from the sidecar, so the scrubber has a length before metadata loads. */
  knownDurationSec?: number | null;
  /** Fired when theatre mode is toggled so the page can go full-bleed. */
  onTheatreChange?: (theatre: boolean) => void;
  className?: string;
};

/**
 * The clip player — control chrome mirrors the app's VideoPlayer (settings
 * popover, volume scrubber, button sizing, playback rates, ambient fill).
 */
export const ClipPlayer = ({
  src,
  poster,
  title,
  knownDurationSec,
  onTheatreChange,
  className
}: ClipPlayerProps): JSX.Element => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const ambientRef = useRef<HTMLCanvasElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  /** The shell as state too: the fullscreen portal host is chosen during render,
   *  where reading a ref is neither reactive nor allowed. */
  const [shellEl, setShellEl] = useState<HTMLDivElement | null>(null);
  const setShellNode = useCallback((node: HTMLDivElement | null) => {
    shellRef.current = node;
    setShellEl(node);
  }, []);
  const seekRef = useRef<HTMLDivElement>(null);
  const volumeRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const theatreFadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const theatreFadingRef = useRef(false);

  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(knownDurationSec ?? 0);
  const [bufferedSec, setBufferedSec] = useState(0);
  const [volume, setVolume] = useState(readStoredVolume);
  const [muted, setMuted] = useState(false);
  const [rate, setRate] = useState(1);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [theatre, setTheatre] = useState(readStoredTheatre);
  const [theatreVisible, setTheatreVisible] = useState(true);
  /** Document / element Fullscreen API (desktop + most Android browsers). */
  const [docFullscreen, setDocFullscreen] = useState(false);
  /** iOS Safari native video fullscreen via webkitEnterFullscreen. */
  const [webkitFullscreen, setWebkitFullscreen] = useState(false);
  /**
   * CSS fixed-viewport fallback when neither API works (older mobile browsers).
   * Keeps our custom chrome, unlike iOS native video fullscreen.
   */
  const [immersive, setImmersive] = useState(false);
  const fullscreen = docFullscreen || webkitFullscreen || immersive;
  /** Viewport orientation as *presented*, which a rotation-locked phone pins. */
  const [portrait, setPortrait] = useState(isPortraitViewport);
  /** Clips are 16:9 game VODs; only a genuinely taller-than-wide one opts out. */
  const [landscapeClip, setLandscapeClip] = useState(true);
  /**
   * Outcome of asking the browser to hold the screen in landscape while
   * fullscreen: "pending" until it answers (so the CSS fallback below doesn't
   * flash on Android, where the lock works), "unavailable" once it has refused.
   */
  const [landscapeLock, setLandscapeLock] = useState<"idle" | "pending" | "locked" | "unavailable">(
    "idle"
  );
  // Capability, not state: resolved once on mount and never changes after.
  const [pipAvailable] = useState(
    () =>
      typeof document !== "undefined" &&
      document.pictureInPictureEnabled === true &&
      !/iPhone|iPad/i.test(navigator.userAgent)
  );
  // Touch-primary devices get the browser's own controls instead of ours:
  // precise scrubbing, a native fullscreen button, and a native play button
  // over the poster. Our overlay is tuned for a mouse and mis-handles taps, so
  // rather than patch it for touch we hand the video off entirely. `pointer:
  // coarse` is true only when the *primary* input is touch (phones, tablets),
  // not on a desktop that merely has a touchscreen.
  const [nativeControls] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches === true
  );
  const [controlsVisible, setControlsVisible] = useState(true);
  const [scrubbing, setScrubbing] = useState(false);
  const [volumeScrubbing, setVolumeScrubbing] = useState(false);
  const [hoverFraction, setHoverFraction] = useState<number | null>(null);
  const [volumeHoverFraction, setVolumeHoverFraction] = useState<number | null>(null);

  const showControls = useCallback((autoHide = true) => {
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (!autoHide) return;
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), IDLE_HIDE_MS);
  }, []);

  useEffect(
    () => () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (theatreFadeTimerRef.current) clearTimeout(theatreFadeTimerRef.current);
    },
    []
  );

  // Apply persisted volume once the element exists / when it changes.
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.volume = volume;
      video.muted = muted;
    }
  }, [volume, muted]);

  useEffect(() => {
    const syncDocFullscreen = (): void => setDocFullscreen(getFullscreenElement() != null);
    document.addEventListener("fullscreenchange", syncDocFullscreen);
    // Older WebKit (iPad / some Android WebViews) still fire the prefixed event.
    document.addEventListener("webkitfullscreenchange", syncDocFullscreen);
    return () => {
      document.removeEventListener("fullscreenchange", syncDocFullscreen);
      document.removeEventListener("webkitfullscreenchange", syncDocFullscreen);
    };
  }, []);

  // iPhone Safari never promotes a div to fullscreen — only the <video> via
  // webkitEnterFullscreen. Track that separately so the button label / layout
  // stay in sync when the user exits with the native "Done" control.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onBegin = (): void => setWebkitFullscreen(true);
    const onEnd = (): void => setWebkitFullscreen(false);
    video.addEventListener("webkitbeginfullscreen", onBegin);
    video.addEventListener("webkitendfullscreen", onEnd);
    return () => {
      video.removeEventListener("webkitbeginfullscreen", onBegin);
      video.removeEventListener("webkitendfullscreen", onEnd);
    };
  }, [src]);

  // Follow the presented orientation so the sideways fallback below can come and
  // go as the viewer turns the handset.
  useEffect(() => {
    const sync = (): void => setPortrait(isPortraitViewport());
    sync();
    return subscribeToOrientation(sync);
  }, []);

  // Turn the phone sideways for the viewer. A handset honours its system
  // rotation lock even in fullscreen, so a viewer who has that on gets a 16:9
  // clip crushed into a portrait letterbox and turning the device does nothing;
  // asking for landscape ourselves is the only way out. It has to run once we
  // are genuinely fullscreen, which rules out the CSS immersive fallback (the
  // browser refuses the lock outside real fullscreen). `nativeControls` doubles
  // as the touch-primary test: desktops are landscape already. The cleanup
  // releases the lock on exit and on unmount.
  useEffect(() => {
    // A portrait clip is already the right way up, and the native iOS video
    // player (webkitFullscreen) draws outside our document, where neither the
    // lock nor the CSS fallback reaches.
    if (!nativeControls || !landscapeClip || !(docFullscreen || immersive)) {
      setLandscapeLock("idle");
      return;
    }
    // The CSS immersive fallback is not real fullscreen, so the lock would be
    // refused; skip straight to rotating the player ourselves.
    if (!docFullscreen) {
      setLandscapeLock("unavailable");
      return;
    }
    let cancelled = false;
    setLandscapeLock("pending");
    void lockLandscapeOrientation().then((locked) => {
      if (!cancelled) setLandscapeLock(locked ? "locked" : "unavailable");
    });
    return () => {
      cancelled = true;
      unlockOrientation();
    };
  }, [nativeControls, landscapeClip, docFullscreen, immersive]);

  // Lock page scroll while the CSS immersive fallback covers the viewport.
  useEffect(() => {
    if (!immersive) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setImmersive(false);
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [immersive]);

  // Tell the page the persisted preference on mount, and again whenever it flips.
  useEffect(() => {
    onTheatreChange?.(theatre);
  }, [theatre, onTheatreChange]);

  // Ambient fill: paint a blurred, zoomed copy of the current frame behind the
  // letterboxed video so the side/top bars aren't dead black (YouTube-style).
  // Samples a small canvas a few times a second; CSS handles the blur + cover.
  // No crossOrigin — we only paint to the screen, never read pixels back, so a
  // tainted canvas is fine and buckets without CORS keep playing.
  useEffect(() => {
    const video = videoRef.current as FrameCallbackVideo | null;
    const canvas = ambientRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;

    let raf = 0;
    let vfc = 0;
    let lastTime = -1;

    // Keep the buffer at the video's aspect so `object-cover` crops it correctly.
    const sizeCanvas = (): boolean => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (w <= 0 || h <= 0) return false;
      const targetH = Math.max(1, Math.round((AMBIENT_SAMPLE_WIDTH * h) / w));
      if (canvas.width !== AMBIENT_SAMPLE_WIDTH || canvas.height !== targetH) {
        canvas.width = AMBIENT_SAMPLE_WIDTH;
        canvas.height = targetH;
      }
      return true;
    };

    // Redraw every presented frame so the fill tracks the video smoothly; skip
    // when the frame hasn't advanced (paused) so it costs nothing at rest.
    const draw = (): void => {
      if (video.readyState < 2 || video.currentTime === lastTime || !sizeCanvas()) return;
      lastTime = video.currentTime;
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      } catch {
        // Transient decode hiccups — keep the last good frame.
      }
    };

    const hasVfc = typeof video.requestVideoFrameCallback === "function";
    const loopVfc = (): void => {
      draw();
      vfc = video.requestVideoFrameCallback!(loopVfc);
    };
    const loopRaf = (): void => {
      draw();
      raf = requestAnimationFrame(loopRaf);
    };
    if (hasVfc) vfc = video.requestVideoFrameCallback!(loopVfc);
    else raf = requestAnimationFrame(loopRaf);

    // Force a redraw when a seek settles (a paused seek presents no rAF frame).
    const onSettled = (): void => {
      lastTime = -1;
      draw();
    };
    video.addEventListener("seeked", onSettled);
    video.addEventListener("loadeddata", onSettled);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (vfc) video.cancelVideoFrameCallback?.(vfc);
      video.removeEventListener("seeked", onSettled);
      video.removeEventListener("loadeddata", onSettled);
    };
  }, [src]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused || video.ended) {
      void video.play().catch(() => setFailed(true));
    } else {
      video.pause();
    }
  }, []);

  const seekBy = useCallback(
    (deltaSec: number) => {
      const video = videoRef.current;
      if (!video || !Number.isFinite(video.duration)) return;
      video.currentTime = Math.min(video.duration, Math.max(0, video.currentTime + deltaSec));
      setCurrentTime(video.currentTime);
      showControls();
    },
    [showControls]
  );

  const seekTo = useCallback(
    (timeSec: number) => {
      const video = videoRef.current;
      if (!video || !Number.isFinite(video.duration)) return;
      video.currentTime = Math.min(video.duration, Math.max(0, timeSec));
      setCurrentTime(video.currentTime);
      showControls();
    },
    [showControls]
  );

  // Lock screen / notification artwork (iOS otherwise uses the tiny SVG favicon).
  useEffect(() => {
    return bindClipMediaSession(title, poster, {
      play: () => {
        const video = videoRef.current;
        if (video) void video.play().catch(() => setFailed(true));
      },
      pause: () => videoRef.current?.pause(),
      seekBy,
      seekTo
    });
  }, [title, poster, seekBy, seekTo]);

  useEffect(() => {
    setClipMediaSessionPlaybackState(playing ? "playing" : "paused");
  }, [playing]);

  useEffect(() => {
    setClipMediaSessionPosition(currentTime, duration, rate);
  }, [currentTime, duration, rate]);

  const setClampedVolume = useCallback((next: number) => {
    const clamped = Math.min(1, Math.max(0, next));
    setVolume(clamped);
    setMuted(false);
    window.localStorage.setItem(VOLUME_STORAGE_KEY, String(clamped));
  }, []);

  const toggleFullscreen = useCallback(() => {
    const shell = shellRef.current;
    const video = videoRef.current;
    if (!shell || !video) return;

    // Exit whichever mode is active.
    if (getFullscreenElement()) {
      void exitElementFullscreen();
      return;
    }
    if (isWebkitVideoFullscreen(video)) {
      exitWebkitVideoFullscreen(video);
      return;
    }
    if (immersive) {
      setImmersive(false);
      return;
    }

    const enterImmersive = (): void => {
      setImmersive(true);
      showControls();
      shell.focus({ preventScroll: true });
    };

    // iOS: deliberately *not* webkitEnterFullscreen here. That hands the clip to
    // the OS video player, which draws outside our document in whatever
    // orientation the page happened to be in — a viewer holding the phone
    // sideways gets a portrait clip and nothing on the page can turn it. Staying
    // in our own DOM (element fullscreen where this iOS has it, else the CSS
    // overlay) keeps the layout ours, so `rotateLandscape` can lay it on its
    // side. The native controls still offer Apple's player for anyone who wants
    // it. Neither call has to be synchronous inside the tap: the capability test
    // is, and entering the CSS overlay is just state.
    if (isAppleTouchDevice()) {
      if (supportsElementFullscreen(shell)) {
        void requestElementFullscreen(shell).then((ok) => {
          if (!ok) enterImmersive();
        });
      } else {
        enterImmersive();
      }
      return;
    }

    // Desktop / Android: element Fullscreen API, then graceful fallbacks.
    void requestElementFullscreen(shell).then((ok) => {
      if (ok) return;
      if (enterWebkitVideoFullscreen(video)) return;
      enterImmersive();
    });
  }, [immersive, showControls]);

  const toggleTheatre = useCallback(() => {
    if (theatreFadingRef.current) return;
    theatreFadingRef.current = true;
    showControls();
    setTheatreVisible(false);

    if (theatreFadeTimerRef.current) clearTimeout(theatreFadeTimerRef.current);
    theatreFadeTimerRef.current = setTimeout(() => {
      setTheatre((value) => {
        const next = !value;
        window.localStorage.setItem(THEATRE_STORAGE_KEY, next ? "1" : "0");
        return next;
      });
      // Two frames so the full-bleed / card layout is applied before we fade in.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTheatreVisible(true);
          theatreFadeTimerRef.current = setTimeout(() => {
            theatreFadingRef.current = false;
            theatreFadeTimerRef.current = null;
          }, THEATRE_FADE_MS);
        });
      });
    }, THEATRE_FADE_MS);
  }, [showControls]);

  const togglePip = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (document.pictureInPictureElement) {
      void document.exitPictureInPicture().catch(() => {});
    } else {
      void video.requestPictureInPicture().catch(() => {});
    }
  }, []);

  const setPlaybackRate = useCallback((next: number) => {
    setRate(next);
    const video = videoRef.current;
    if (video) video.playbackRate = next;
  }, []);

  // Shortcuts work anywhere on the page, not just when the player is focused —
  // the clip is the whole page, so a listener on the window is what a viewer
  // expects. Native-controls (touch) devices are left to the browser. Typing
  // surfaces and modifier chords are ignored, and Space/Enter still activate a
  // focused button or link rather than being hijacked.
  useEffect(() => {
    if (nativeControls) return;
    const handler = (event: KeyboardEvent): void => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      const tag = el?.tagName;
      if (el?.isContentEditable || tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        return;
      }
      const onControl = tag === "BUTTON" || tag === "A" || el?.getAttribute("role") === "button";
      switch (event.key) {
        case " ":
        case "k":
          if (event.key === " " && onControl) return;
          event.preventDefault();
          togglePlay();
          break;
        case "ArrowLeft":
          event.preventDefault();
          seekBy(-5);
          break;
        case "ArrowRight":
          event.preventDefault();
          seekBy(5);
          break;
        case "j":
          seekBy(-10);
          break;
        case "l":
          seekBy(10);
          break;
        case "ArrowUp":
          event.preventDefault();
          setClampedVolume(volume + 0.05);
          break;
        case "ArrowDown":
          event.preventDefault();
          setClampedVolume(volume - 0.05);
          break;
        case "m":
          setMuted((value) => !value);
          break;
        case "f":
          toggleFullscreen();
          break;
        case "t":
          if (!fullscreen) toggleTheatre();
          break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    nativeControls,
    togglePlay,
    seekBy,
    setClampedVolume,
    volume,
    toggleFullscreen,
    toggleTheatre,
    fullscreen
  ]);

  const seekToFraction = useCallback((fraction: number) => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;
    video.currentTime = fraction * video.duration;
    setCurrentTime(video.currentTime);
  }, []);

  const onSeekPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const track = seekRef.current;
      if (!track) return;
      track.setPointerCapture(event.pointerId);
      setScrubbing(true);
      seekToFraction(fractionAt(track, event.clientX));
    },
    [seekToFraction]
  );

  const onSeekPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const track = seekRef.current;
      if (!track) return;
      const fraction = fractionAt(track, event.clientX);
      setHoverFraction(fraction);
      if (scrubbing) seekToFraction(fraction);
    },
    [scrubbing, seekToFraction]
  );

  const onSeekPointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    seekRef.current?.releasePointerCapture(event.pointerId);
    setScrubbing(false);
  }, []);

  const onVolumePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const track = volumeRef.current;
      if (!track) return;
      track.setPointerCapture(event.pointerId);
      setVolumeScrubbing(true);
      setClampedVolume(fractionAt(track, event.clientX));
    },
    [setClampedVolume]
  );

  const onVolumePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const track = volumeRef.current;
      if (!track) return;
      const fraction = fractionAt(track, event.clientX);
      setVolumeHoverFraction(fraction);
      if (volumeScrubbing) setClampedVolume(fraction);
    },
    [volumeScrubbing, setClampedVolume]
  );

  const onVolumePointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    volumeRef.current?.releasePointerCapture(event.pointerId);
    setVolumeScrubbing(false);
  }, []);

  const playedFraction = duration > 0 ? currentTime / duration : 0;
  const bufferedFraction = duration > 0 ? Math.min(1, bufferedSec / duration) : 0;
  const volumeFraction = muted ? 0 : volume;
  const controlsShown = controlsVisible || !playing || scrubbing || volumeScrubbing || settingsOpen;

  /**
   * Last resort for landscape: lay the player on its side ourselves.
   *
   * We get here when the clip is fullscreen inside our own document on a phone,
   * the clip is landscape, the browser would not hold the screen in landscape
   * for us (Safari has no working orientation lock), and the viewport is still
   * portrait — which is what a viewer holding the handset sideways sees when the
   * page is not allowed to turn. Rotating the shell 90° puts the video the way
   * up they are already holding the device. It undoes itself the moment the
   * viewport does report landscape, so a phone that rotates normally never hits
   * this path.
   */
  const rotateLandscape =
    nativeControls &&
    (docFullscreen || immersive) &&
    landscapeClip &&
    portrait &&
    landscapeLock === "unavailable";

  // Fullscreen lives in a top layer — portal settings into the player itself.
  // Otherwise portal to the document body so overflow-hidden on the shell
  // cannot clip the popover (the app uses a dedicated menus host for this).
  const settingsPortalHost = fullscreen ? shellEl : undefined;

  const settingsPanel = (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <div className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
          Speed
        </div>
        <div className="grid grid-cols-5 gap-1">
          {PLAYBACK_RATES.map((value) => {
            const active = rate === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={active}
                onClick={() => setPlaybackRate(value)}
                className={cn(
                  "h-8 cursor-pointer rounded-md text-xs font-bold tabular-nums transition-colors",
                  active
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground hover:bg-accent"
                )}
              >
                {value}x
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const settingsTriggerClass = cn(
    "flex size-8 cursor-pointer items-center justify-center rounded-md hover:bg-white/15",
    settingsOpen && "bg-white/15"
  );

  return (
    <div
      ref={setShellNode}
      tabIndex={-1}
      onPointerDown={() => showControls()}
      onPointerMove={() => showControls()}
      onPointerLeave={() => {
        if (playing) setControlsVisible(false);
      }}
      className={cn(
        "group/player relative isolate flex min-h-0 w-full items-center justify-center bg-black outline-none transition-opacity ease-out",
        // Default: 16:9 card. Theatre: full-bleed strip under the header, capped
        // by viewport height so ultrawide leaves side gutters for ambient fill.
        // The 13rem reserve keeps the header plus the title/meta block below the
        // video on screen without scrolling, instead of cropping them off.
        // Fullscreen / immersive: fill the monitor (or the mobile viewport).
        // Sideways: the rotated box below owns the sizing, so leave it alone here.
        rotateLandscape
          ? "overflow-visible rounded-none"
          : fullscreen || immersive
            ? "size-full overflow-visible rounded-none"
            : theatre
              ? "h-[min(56.25vw,calc(100svh-13rem))] overflow-hidden rounded-none"
              : "aspect-video overflow-hidden rounded-xl",
        // CSS fallback when the Fullscreen API is unavailable (common on phones).
        immersive &&
          !rotateLandscape &&
          "fixed inset-0 z-[100] h-dvh max-h-dvh w-screen rounded-none",
        // Sideways (see rotateLandscape): the box swaps its axes — as wide as
        // the viewport is tall and vice versa — and turns about its top-left
        // corner, which swings it off to the left, so it is pushed back right by
        // one viewport width. Listed last so `cn` drops the sizing/position
        // utilities above that it conflicts with.
        rotateLandscape &&
          "fixed top-0 left-0 z-[100] h-[100dvw] max-h-none w-[100dvh] max-w-none origin-top-left translate-x-[100dvw] rotate-90",
        theatreVisible ? "opacity-100 duration-100" : "opacity-0 duration-75",
        !controlsShown && "cursor-none",
        className
      )}
    >
      {/* Ambient backdrop — sits above the black bg, below the video (-z-10 + isolate). */}
      <canvas
        ref={ambientRef}
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 size-full scale-125 object-cover blur-2xl brightness-[0.55] saturate-150"
      />

      <video
        ref={videoRef}
        src={src}
        poster={poster ?? undefined}
        title={title}
        playsInline
        controls={nativeControls}
        preload="metadata"
        // Deliberately not crossOrigin: nothing here reads pixels back from the
        // ambient canvas, and asking for CORS would make playback fail outright
        // on any bucket that has no CORS policy set, which is most of them.
        className={cn(
          "relative z-0 size-full object-contain",
          // Suppress the UA chrome only when we draw our own; on touch it IS the UI.
          !nativeControls && "no-native-controls"
        )}
        // With native controls a tap belongs to the browser (show/hide, seek);
        // our play/fullscreen toggles would fight it, so they attach only on
        // pointer devices.
        onClick={nativeControls ? undefined : togglePlay}
        onDoubleClick={nativeControls ? undefined : toggleFullscreen}
        onPlay={() => {
          setPlaying(true);
          setEnded(false);
          showControls();
        }}
        onPause={() => {
          setPlaying(false);
          showControls(false);
        }}
        onEnded={() => {
          setEnded(true);
          setPlaying(false);
          showControls(false);
        }}
        onWaiting={() => setWaiting(true)}
        onPlaying={() => setWaiting(false)}
        onCanPlay={() => setWaiting(false)}
        onError={() => setFailed(true)}
        onLoadedMetadata={(event) => {
          const media = event.currentTarget;
          if (Number.isFinite(media.duration)) setDuration(media.duration);
          // Dimensions of 0 mean the decoder has not reported yet; assume
          // landscape, which every game VOD is.
          setLandscapeClip(media.videoWidth <= 0 || media.videoWidth >= media.videoHeight);
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onProgress={(event) => {
          const video = event.currentTarget;
          const ranges = video.buffered;
          if (ranges.length === 0) return;
          for (let i = ranges.length - 1; i >= 0; i -= 1) {
            if (ranges.start(i) <= video.currentTime) {
              setBufferedSec(ranges.end(i));
              return;
            }
          }
          setBufferedSec(ranges.end(ranges.length - 1));
        }}
      />

      {/* Big center play / replay affordance while paused. Native controls draw
          their own play button over the poster, so ours is pointer-only. */}
      {!nativeControls && !playing && !failed ? (
        <button
          type="button"
          aria-label={ended ? "Replay" : "Play"}
          onClick={togglePlay}
          className="absolute inset-0 m-auto flex size-16 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white transition-transform hover:scale-105"
        >
          {ended ? <RotateCcw className="size-7" /> : <Play className="ml-1 size-7 fill-current" />}
        </button>
      ) : null}

      {!nativeControls && waiting && !failed ? (
        <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center">
          <Loader2 className="size-10 animate-spin text-white/80" />
        </div>
      ) : null}

      {failed ? (
        <div className="absolute inset-0 z-20 grid place-items-center bg-black/80 px-6 text-center">
          <div className="max-w-sm space-y-2">
            <p className="text-sm font-semibold text-white">This clip could not be played.</p>
            <p className="text-xs text-white/60">
              The file may have been removed from the bucket, or the bucket may no longer be public.
            </p>
          </div>
        </div>
      ) : null}

      {/* Touch devices hand the chrome to the browser, and its fullscreen button
          hands the clip to the OS video player — which on iOS keeps whatever
          orientation the page was in, so a viewer holding the phone sideways
          still gets a portrait clip and no way to turn it. This is our own way
          in: it keeps the clip in our document, where the landscape fallback can
          lay it on its side. */}
      {nativeControls ? (
        <button
          type="button"
          aria-label={fullscreen ? "Exit fullscreen" : "Fullscreen"}
          onClick={toggleFullscreen}
          className={cn(
            "absolute top-2 right-2 z-10 flex size-10 items-center justify-center rounded-full bg-black/55 text-white transition-opacity",
            controlsShown ? "opacity-100" : "pointer-events-none opacity-0"
          )}
        >
          {fullscreen ? <Minimize className="size-5" /> : <Maximize className="size-5" />}
        </button>
      ) : null}

      {/* Control bar — pointer devices only; touch uses the native chrome. */}
      {!nativeControls ? (
        <div
          className={cn(
            "absolute inset-x-0 bottom-0 flex flex-col gap-1.5 bg-gradient-to-t from-black/85 via-black/50 to-transparent px-2 pt-8 pb-[max(0.625rem,env(safe-area-inset-bottom))] transition-opacity sm:px-3 sm:pb-2.5",
            controlsShown ? "opacity-100" : "pointer-events-none opacity-0"
          )}
        >
          {/* Seek bar */}
          <div
            ref={seekRef}
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(currentTime)}
            onPointerDown={onSeekPointerDown}
            onPointerMove={onSeekPointerMove}
            onPointerUp={onSeekPointerUp}
            onPointerLeave={() => setHoverFraction(null)}
            className="group/seek relative flex h-4 cursor-pointer items-center touch-none"
          >
            <div className="relative h-1 w-full overflow-hidden rounded-full bg-white/25 transition-all group-hover/seek:h-1.5">
              {bufferedFraction > 0 ? (
                <div
                  className="absolute inset-y-0 left-0 bg-white/30"
                  style={{ width: `${bufferedFraction * 100}%` }}
                />
              ) : null}
              {hoverFraction != null ? (
                <div
                  className="absolute inset-y-0 left-0 bg-white/40"
                  style={{ width: `${hoverFraction * 100}%` }}
                />
              ) : null}
              <div
                className="absolute inset-y-0 left-0 bg-primary"
                style={{ width: `${playedFraction * 100}%` }}
              />
            </div>
            <div
              className="absolute size-3 -translate-x-1/2 rounded-full bg-primary opacity-0 transition-opacity group-hover/seek:opacity-100"
              style={{ left: `${playedFraction * 100}%` }}
            />
            {hoverFraction != null && duration > 0 ? (
              <div
                className="pointer-events-none absolute bottom-5 -translate-x-1/2 rounded-md bg-black/90 px-1.5 py-0.5 text-[11px] font-medium text-white tabular-nums"
                style={{ left: `${hoverFraction * 100}%` }}
              >
                {formatDuration(hoverFraction * duration)}
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-0.5 text-white sm:gap-1">
            <Tip label={playing ? "Pause" : "Play"} side="top">
              <button
                type="button"
                aria-label={playing ? "Pause" : "Play"}
                onClick={togglePlay}
                className="flex size-10 cursor-pointer items-center justify-center rounded-md hover:bg-white/15 sm:size-8"
              >
                {playing ? (
                  <Pause className="size-5 fill-current sm:size-4.5" />
                ) : (
                  <Play className="size-5 fill-current sm:size-4.5" />
                )}
              </button>
            </Tip>

            <div className="group/volume flex items-center">
              <Tip label={muted ? "Unmute" : "Mute"} side="top">
                <button
                  type="button"
                  aria-label={muted ? "Unmute" : "Mute"}
                  onClick={() => setMuted((value) => !value)}
                  className="flex size-10 cursor-pointer items-center justify-center rounded-md hover:bg-white/15 sm:size-8"
                >
                  {muted || volume === 0 ? (
                    <VolumeX className="size-5 sm:size-4.5" />
                  ) : (
                    <Volume2 className="size-5 sm:size-4.5" />
                  )}
                </button>
              </Tip>
              <div
                ref={volumeRef}
                role="slider"
                aria-label="Volume"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(volumeFraction * 100)}
                onPointerDown={onVolumePointerDown}
                onPointerMove={onVolumePointerMove}
                onPointerUp={onVolumePointerUp}
                onPointerLeave={() => setVolumeHoverFraction(null)}
                className={cn(
                  "group/vol-seek relative flex h-4 cursor-pointer items-center touch-none transition-all duration-200",
                  // Touch phones have no hover — keep a compact bar visible.
                  // Fine pointers keep the hover-to-expand desktop behaviour.
                  "w-14 opacity-100",
                  "[@media(hover:hover)_and_(pointer:fine)]:w-0 [@media(hover:hover)_and_(pointer:fine)]:opacity-0",
                  "[@media(hover:hover)_and_(pointer:fine)]:group-hover/volume:w-20",
                  "[@media(hover:hover)_and_(pointer:fine)]:group-hover/volume:opacity-100",
                  volumeScrubbing && "w-20 opacity-100"
                )}
              >
                <div className="relative h-1 w-full overflow-hidden rounded-full bg-white/25 transition-all group-hover/vol-seek:h-1.5">
                  {volumeHoverFraction != null ? (
                    <div
                      className="absolute inset-y-0 left-0 bg-white/40"
                      style={{ width: `${volumeHoverFraction * 100}%` }}
                    />
                  ) : null}
                  <div
                    className="absolute inset-y-0 left-0 bg-primary"
                    style={{ width: `${volumeFraction * 100}%` }}
                  />
                </div>
                <div
                  className={cn(
                    "absolute size-3 -translate-x-1/2 rounded-full bg-primary opacity-0 transition-opacity group-hover/vol-seek:opacity-100",
                    volumeScrubbing && "opacity-100"
                  )}
                  style={{ left: `${volumeFraction * 100}%` }}
                />
                {volumeScrubbing ? (
                  <div
                    className="pointer-events-none absolute bottom-5 -translate-x-1/2 rounded-md bg-black/90 px-1.5 py-0.5 text-[11px] font-medium text-white tabular-nums"
                    style={{ left: `${volumeFraction * 100}%` }}
                  >
                    {Math.round(volumeFraction * 100)}%
                  </div>
                ) : null}
              </div>
            </div>

            <span className="ml-1 shrink-0 text-[11px] font-medium text-white/90 tabular-nums sm:ml-1.5 sm:text-xs">
              {formatDuration(currentTime)} / {formatDuration(duration)}
            </span>

            <div className="flex-1" />

            <div data-player-settings="" className="relative">
              <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="Player settings"
                    className={cn(settingsTriggerClass, "size-10 sm:size-8")}
                  >
                    {rate !== 1 ? (
                      <span className="text-xs font-bold tabular-nums">{rate}x</span>
                    ) : (
                      <Settings className="size-5 sm:size-4.5" />
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="end"
                  sideOffset={10}
                  container={settingsPortalHost}
                  className="w-64 p-3"
                  onOpenAutoFocus={(event) => event.preventDefault()}
                >
                  {settingsPanel}
                </PopoverContent>
              </Popover>
            </div>

            {pipAvailable ? (
              <Tip label="Picture in picture" side="top">
                <button
                  type="button"
                  aria-label="Picture in picture"
                  onClick={togglePip}
                  className="hidden size-8 cursor-pointer items-center justify-center rounded-md hover:bg-white/15 sm:flex"
                >
                  <PictureInPicture2 className="size-4.5" />
                </button>
              </Tip>
            ) : null}

            {/* Theatre is a desktop layout affordance — phones are already full-bleed. */}
            {!fullscreen ? (
              <Tip label={theatre ? "Exit theatre mode" : "Theatre mode"} side="top">
                <button
                  type="button"
                  aria-label={theatre ? "Exit theatre mode" : "Theatre mode"}
                  aria-pressed={theatre}
                  onClick={toggleTheatre}
                  className={cn(
                    "hidden size-8 cursor-pointer items-center justify-center rounded-md hover:bg-white/15 sm:flex",
                    theatre && "bg-white/15"
                  )}
                >
                  <RectangleHorizontal className="size-4.5" />
                </button>
              </Tip>
            ) : null}

            <Tip label={fullscreen ? "Exit fullscreen" : "Fullscreen"} side="top">
              <button
                type="button"
                aria-label={fullscreen ? "Exit fullscreen" : "Fullscreen"}
                onClick={toggleFullscreen}
                className="flex size-10 cursor-pointer items-center justify-center rounded-md hover:bg-white/15 sm:size-8"
              >
                {fullscreen ? (
                  <Minimize className="size-5 sm:size-4.5" />
                ) : (
                  <Maximize className="size-5 sm:size-4.5" />
                )}
              </button>
            </Tip>
          </div>
        </div>
      ) : null}
    </div>
  );
};
